import React from 'react';
import ReactDOM from 'react-dom';
import { normalizeText } from '@/components/admin/SlideShow/Slide/Utils';
import SectionTextGlossaryTerm
  from '@/components/shared/Froala/plugins/GlossaryTerms/SectionTextGlossaryTerm/SectionTextGlossaryTerm';

// New Builder:
// Load all new builder glossary terms on page load
export const loadNewBuilderGlossaryTerms = () => {
  // Find all glossary term containers (parent elements with data-term-id)
  const glossaryContainers = document.querySelectorAll('[data-term-id]');

  glossaryContainers.forEach((container) => {
    const parentElement = container as HTMLElement;

    // Check if already mounted to avoid re-rendering and memory leaks
    if (parentElement.dataset.glossaryMounted === 'true') {
      return;
    }

    const termIdStr = parentElement.getAttribute('data-term-id');
    if (!termIdStr) {
      console.warn('Glossary term missing data-term-id attribute', parentElement);
      return;
    }

    const termId = parseInt(termIdStr, 10);
    if (isNaN(termId)) {
      console.warn('Glossary term has invalid data-term-id', termIdStr, parentElement);
      return;
    }

    // Find the original text from the strong.glossary-text element
    const strongElement = parentElement.querySelector('strong.glossary-text');
    if (!strongElement) {
      console.warn('Glossary term missing strong.glossary-text element', parentElement);
      return;
    }

    const originalText = strongElement.textContent || '';

    // Mark as mounted before rendering to prevent duplicate renders
    parentElement.dataset.glossaryMounted = 'true';

    ReactDOM.render(
      <SectionTextGlossaryTerm displayDefinitionOnLoad={false} originalText={originalText} termId={termId} />,
      parentElement,
    );
  });
};

// Old Builder:
// Initialize old builder glossary terms.
// 1. Add normalized .glossary class to terms ont he page.
// 2. Definitions by default are initialized at the bottom of the page.
// 3. Move the definitions to the appropriate place after the glossary term.
export const initializeOldBuilderGlossaryTerms = () => {
  const glossarySpans = document.querySelectorAll('span.glossary');

  glossarySpans.forEach((span) => {
    const termText = span.textContent?.trim() || '';
    const normalizedTerm = normalizeText(termText);

    // Add dynamic class to the term span
    span.classList.add(`glossary_${normalizedTerm}`);

    // Find matching definitions and move them after the term
    const definitions = document.querySelectorAll(`.glossary_${normalizedTerm}.definition`);
    definitions.forEach((def) => {
      span.parentNode?.insertBefore(def, span.nextSibling);
    });
  });
};

// Old Builder:
// Handle close button clicks for old builder image-based glossaries
export const handleOldBuilderCloseButton = (target: HTMLElement): boolean => {
  if (!target.classList.contains('close_box')) return false;

  const definitionDiv = target.closest('.definition');
  if (definitionDiv) {
    (definitionDiv as HTMLElement).style.display = 'none';
  }
  return true;
};

// Old builder:
// Toggle visibility of separate definition elements
// Basically copies "GlossaryTerms.js" functionality.
export const handleOldBuilderGlossaryToggle = (target: HTMLElement): boolean => {
  let glossarySpan: HTMLElement | null = null;
  if (target.classList.contains('glossary')) {
    glossarySpan = target;
  } else if (target.parentElement?.classList.contains('glossary')) {
    glossarySpan = target.parentElement;
  }

  if (!glossarySpan) return false;

  // Extract and normalize the term to find matching definition
  const termText = glossarySpan.textContent?.trim() || '';
  const normalizedTerm = normalizeText(termText);

  // Find all definitions with this class (may have singular/plural forms)
  const definitions = document.querySelectorAll(`.glossary_${normalizedTerm}.definition`);

  if (definitions.length > 0) {
    definitions.forEach((def) => {
      const defElement = def as HTMLElement;
      if (defElement.style.display === 'none' || !defElement.style.display) {
        defElement.style.display = defElement.classList.contains('glossary_image') ? 'block' : 'inline';
      } else {
        defElement.style.display = 'none';
      }
    });
    return true;
  }

  return false;
};

// Old Builder:
// Note: New builder glossary terms are loaded on page load via loadNewBuilderGlossaryTerms()
export const handleTextSectionClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
  const target = e.target as HTMLElement;

  if (handleOldBuilderCloseButton(target)) return;

  handleOldBuilderGlossaryToggle(target);
};

const HIGHLIGHTED = { color: '#3434C8', fontWeight: 'bold' };

// Toggle main idea styles in reading section text.
// Uses inline styles because .main-idea spans in the HTML may have inline styles that override class-based styles.
// AIDEV-NOTE: PLAT-18017 — mirrors modules/MainIdea.js: legacy .mainideas spans reset to the body
// colour rather than cleared, or their own inline styles show through.
export const toggleMainIdeas = (mainIdeasOpen: boolean) => {
  const styleIdeas = (selector: string, cleared: { color: string, fontWeight: string }) => {
    document.querySelectorAll(selector).forEach((idea) => {
      const el = idea as HTMLElement;
      const { color, fontWeight } = mainIdeasOpen ? cleared : HIGHLIGHTED;

      el.style.color = color;
      el.style.fontWeight = fontWeight;
    });
  };

  styleIdeas('.main-idea', { color: '', fontWeight: '' });
  styleIdeas('.mainideas', { color: '#3c3c3c', fontWeight: 'normal' });
};
